import type { Bar } from '../core/types'

// Robust OHLC CSV parser. Auto-detects delimiter, header, and time format so it
// can ingest TradingView "Download chart data" exports (ISO `2020-07-21T06:00:00Z`
// or UNIX timestamps) as well as common local data (MT4/MT5 `YYYY.MM.DD,HH:MM`,
// generic `time,open,high,low,close,volume`). All times are normalized to UTC
// epoch SECONDS. Extra columns (indicator plots) are ignored.

export type TimeFormat = 'iso' | 'unix-s' | 'unix-ms' | 'unix-us' | 'datetime' | 'ymd-hms'

export interface ParseOptions {
  /** Minutes to ADD to convert source local time to UTC (e.g. HistData EST = +300). Default 0 (source already UTC). */
  utcOffsetMinutes?: number
}

export interface ParseResult {
  bars: Bar[]
  detected: {
    delimiter: string
    hasHeader: boolean
    timeFormat: TimeFormat
    columns: { time: number; date?: number; open: number; high: number; low: number; close: number; volume?: number }
  }
  warnings: string[]
}

const NUMERIC = /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/
const ISO_LIKE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/
const DATE_ONLY = /^\d{4}[.\-/]\d{2}[.\-/]\d{2}$/
const TIME_ONLY = /^\d{1,2}:\d{2}(:\d{2})?$/
const YMD_HMS = /^(\d{4})(\d{2})(\d{2})[ T]?(\d{2})(\d{2})(\d{2})$/

function detectDelimiter(line: string): string {
  const counts: Record<string, number> = {
    ',': (line.match(/,/g) || []).length,
    ';': (line.match(/;/g) || []).length,
    '\t': (line.match(/\t/g) || []).length,
  }
  let best = ','
  let max = -1
  for (const d of [',', ';', '\t']) {
    if (counts[d]! > max) {
      max = counts[d]!
      best = d
    }
  }
  return best
}

function looksNumeric(s: string): boolean {
  return NUMERIC.test(s.trim())
}

/** Parse a single time cell (or date+time pair) to UTC epoch seconds, or NaN. */
export function parseTimeCell(
  timeStr: string,
  timeColFormat: TimeFormat,
  offsetMin: number,
  dateStr?: string,
): number {
  const s = timeStr.trim()
  switch (timeColFormat) {
    case 'unix-us':
      return Math.round(Number(s) / 1_000_000) - offsetMin * 60
    case 'unix-ms':
      return Math.round(Number(s) / 1_000) - offsetMin * 60
    case 'unix-s':
      return Math.round(Number(s)) - offsetMin * 60
    case 'ymd-hms': {
      const m = YMD_HMS.exec(s)
      if (!m) return NaN
      const ms = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!)
      return Math.round(ms / 1000) - offsetMin * 60
    }
    case 'datetime': {
      // separate date column + time column, e.g. "2020.01.02" + "13:30"
      const d = (dateStr ?? '').trim().replace(/[./]/g, '-')
      const ms = Date.parse(`${d}T${s.length === 5 ? s + ':00' : s}Z`)
      return Number.isNaN(ms) ? NaN : Math.round(ms / 1000) - offsetMin * 60
    }
    case 'iso':
    default: {
      // Force UTC when no timezone is present (TradingView ISO already has 'Z').
      const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s)
      const iso = hasTz ? s.replace(' ', 'T') : s.replace(' ', 'T') + 'Z'
      const ms = Date.parse(iso)
      return Number.isNaN(ms) ? NaN : Math.round(ms / 1000) - offsetMin * 60
    }
  }
}

function classifyNumericTime(sample: string): TimeFormat {
  const n = Math.abs(Number(sample))
  if (n >= 1e15) return 'unix-us'
  if (n >= 1e12) return 'unix-ms'
  return 'unix-s'
}

export function parseOhlcCsv(text: string, opts: ParseOptions = {}): ParseResult {
  const offsetMin = opts.utcOffsetMinutes ?? 0
  const warnings: string[] = []
  const rawLines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0)
  if (rawLines.length === 0) return emptyResult('iso')

  const delimiter = detectDelimiter(rawLines[0]!)
  const split = (l: string) => l.split(delimiter).map((c) => c.trim())

  // Header detection: a first row whose OHLC-position cells are non-numeric.
  const first = split(rawLines[0]!)
  const headerLikely = first.some((c) => /open|high|low|close|vol|time|date/i.test(c))
  const firstAllNonNumericLater = first.slice(1).every((c) => !looksNumeric(c))
  const hasHeader = headerLikely && firstAllNonNumericLater

  let header: string[] | null = hasHeader ? first.map((c) => c.toLowerCase()) : null
  const dataLines = hasHeader ? rawLines.slice(1) : rawLines
  if (dataLines.length === 0) return emptyResult('iso')

  const sample = split(dataLines[0]!)

  // Resolve column indices.
  let cTime = 0
  let cDate: number | undefined
  let cOpen = 1
  let cHigh = 2
  let cLow = 3
  let cClose = 4
  let cVol: number | undefined = sample.length > 5 ? 5 : undefined

  if (header) {
    const find = (...keys: string[]) =>
      header!.findIndex((h) => keys.some((k) => h === k || h.includes(k)))
    const ti = find('time', 'timestamp', 'datetime', 'unix')
    const di = find('date')
    const oi = find('open')
    const hi = find('high')
    const li = find('low')
    const ci = find('close')
    const vi = find('volume', 'vol')
    if (oi >= 0) cOpen = oi
    if (hi >= 0) cHigh = hi
    if (li >= 0) cLow = li
    if (ci >= 0) cClose = ci
    cVol = vi >= 0 ? vi : undefined
    // date + time split (MT-style) vs single time/datetime
    if (di >= 0 && ti >= 0 && di !== ti) {
      cDate = di
      cTime = ti
    } else if (ti >= 0) {
      cTime = ti
    } else if (di >= 0) {
      cTime = di
    }
  } else {
    // No header: detect a separate date,time pair at the front (MT4: 2020.01.02,13:30,O,H,L,C,V)
    if (DATE_ONLY.test(sample[0] ?? '') && TIME_ONLY.test(sample[1] ?? '')) {
      cDate = 0
      cTime = 1
      cOpen = 2
      cHigh = 3
      cLow = 4
      cClose = 5
      cVol = sample.length > 6 ? 6 : undefined
    }
  }

  // Determine time format from the sample time cell.
  const timeSample = (sample[cTime] ?? '').trim()
  let timeFormat: TimeFormat
  if (cDate !== undefined) timeFormat = 'datetime'
  else if (NUMERIC.test(timeSample)) timeFormat = classifyNumericTime(timeSample)
  else if (YMD_HMS.test(timeSample)) timeFormat = 'ymd-hms'
  else if (ISO_LIKE.test(timeSample) || !Number.isNaN(Date.parse(timeSample))) timeFormat = 'iso'
  else timeFormat = 'iso'

  const bars: Bar[] = []
  let dropped = 0
  for (const line of dataLines) {
    const cols = split(line)
    const t = parseTimeCell(cols[cTime] ?? '', timeFormat, offsetMin, cDate !== undefined ? cols[cDate] : undefined)
    const open = Number(cols[cOpen])
    const high = Number(cols[cHigh])
    const low = Number(cols[cLow])
    const close = Number(cols[cClose])
    const volume = cVol !== undefined ? Number(cols[cVol]) || 0 : 0
    if (
      !Number.isFinite(t) ||
      !Number.isFinite(open) ||
      !Number.isFinite(high) ||
      !Number.isFinite(low) ||
      !Number.isFinite(close) ||
      high < low ||
      high < open ||
      high < close ||
      low > open ||
      low > close ||
      open <= 0
    ) {
      dropped++
      continue
    }
    bars.push({ time: t, open, high, low, close, volume })
  }

  bars.sort((a, b) => a.time - b.time)
  // dedupe by time (keep last)
  const deduped: Bar[] = []
  for (const b of bars) {
    const prev = deduped[deduped.length - 1]
    if (prev && prev.time === b.time) deduped[deduped.length - 1] = b
    else deduped.push(b)
  }

  if (dropped > 0) warnings.push(`dropped ${dropped} invalid row(s)`)
  if (deduped.length !== bars.length) warnings.push(`merged ${bars.length - deduped.length} duplicate timestamp(s)`)
  if (deduped.length === 0) warnings.push('no valid bars parsed')

  return {
    bars: deduped,
    detected: {
      delimiter: delimiter === '\t' ? 'tab' : delimiter,
      hasHeader,
      timeFormat,
      columns: { time: cTime, date: cDate, open: cOpen, high: cHigh, low: cLow, close: cClose, volume: cVol },
    },
    warnings,
  }
}

function emptyResult(tf: TimeFormat): ParseResult {
  return {
    bars: [],
    detected: { delimiter: ',', hasHeader: false, timeFormat: tf, columns: { time: 0, open: 1, high: 2, low: 3, close: 4 } },
    warnings: ['no valid bars parsed'],
  }
}
